import { FileDto, Mark43File, UserExport } from '@mark43/cad-api';
import { getRealUrlPrefix } from '~/session';
import { differenceInMinutes, isValid, parseISO } from 'date-fns';
import { FILE_WEB_SERVER_PATH_TTL_MINUTES } from '../../exports/constants';

export function getFilePath(
    file: FileDto | Mark43File | undefined,
    mediaAttachmentsEnabled: boolean
): string {
    if (!mediaAttachmentsEnabled) {
        // For exports, we intentionally keep using `fileAppServerPath` when attachments are OFF.
        // `fileWebServerPath` links expire after 50 minutes and want to preserve existing behavior for now when attachments is off.
        // TODO Remove reference to file?.fileAppServerPath when attachments / exports api return only a single path `fileWebServerPath`
        return file?.fileAppServerPath || '';
    }
    const path = file?.fileWebServerPath || '';

    if (!path) {
        return '';
    }

    const prefix = getRealUrlPrefix();
    if (!prefix) {
        return '';
    }

    // `new URL(relative, base)` treats a base without a trailing slash as a "file" URL and
    // replaces the last path segment during resolution.
    // Example:
    //   new URL('s3/file.jpg', 'https://example.com/api').toString() => 'https://example.com/s3/file.jpg'   (drops '/api')
    // Ensuring the base ends with '/' forces it to be treated as a directory base.
    const base = prefix.endsWith('/') ? prefix : `${prefix}/`;

    try {
        return new URL(path, base).toString();
    } catch {
        return '';
    }
}

/**
 * Determines if a URL is an RMS streaming URL (long-lived).
 * RMS stream URLs don't expire after 50 minutes.
 */
export function isRmsStreamUrl(url: string | undefined): boolean {
    if (!url) return false;
    return url.includes('rms/api/files/stream');
}

/**
 * Determines if an export uses a short-lived (50-minute) download path.
 * This happens when:
 * - mediaAttachmentsEnabled is true AND
 * - the resolved file path is NOT an RMS streaming URL (presigned URL instead)
 *
 * @param file - The file object from the export
 * @param mediaAttachmentsEnabled - Feature flag for media attachments
 * @returns true if the export link expires in 50 minutes
 */
export function isPresignedUrlExport(
    file: FileDto | Mark43File | undefined,
    mediaAttachmentsEnabled: boolean
): boolean {
    if (!mediaAttachmentsEnabled) {
        // When attachments disabled, we use fileAppServerPath which is long-lived
        return false;
    }

    // Get the actual path that would be used for download
    const resolvedPath = getFilePath(file, mediaAttachmentsEnabled);

    // If there's no path, it's not a short-lived export (might be processing)
    if (!resolvedPath) {
        return false;
    }

    // If it's an RMS stream URL, it's long-lived (12 hours)
    // If it's a presigned URL (not RMS stream), it's short-lived (50 minutes)
    return !isRmsStreamUrl(resolvedPath);
}

/**
 * Determines if an export's download URL has expired due to short-lived
 * presigned URL expiration (50 minutes).
 *
 * This is a frontend-only check to hide exports with expired download links.
 * Returns false for long-lived exports or when unable to determine age.
 *
 * @param userExport - The user export object
 * @param mediaAttachmentsEnabled - Feature flag for media attachments
 * @returns true if the export uses a short-lived presigned URL AND the URL has expired (>= 50 minutes)
 */
export function isExportExpired(userExport: UserExport, mediaAttachmentsEnabled: boolean): boolean {
    // Only short-lived presigned URLs expire in 50 minutes
    const usePresignedUrl = isPresignedUrlExport(userExport.file, mediaAttachmentsEnabled);
    if (!usePresignedUrl) {
        return false;
    }

    // Get creation date with fallback
    const createdDateUtc = userExport.file?.createdDateUtc || userExport.createdDateUtc;
    if (!createdDateUtc) {
        return false;
    }

    const createdDateParsed = parseISO(createdDateUtc);
    if (!isValid(createdDateParsed)) {
        return false;
    }

    const ageInMinutes = differenceInMinutes(new Date(), createdDateParsed);

    return ageInMinutes >= FILE_WEB_SERVER_PATH_TTL_MINUTES;
}
